
import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Brain, TrendingUp, AlertCircle } from 'lucide-react';
import { toast } from "sonner";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';

interface ExamReadiness {
  readiness: number;
  confidence: number;
  recommendations: string[];
  trendData: any[];
}

export const PredictiveAnalysis = () => {
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    const fetchUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setUserId(user?.id ?? null);
    };
    fetchUser();
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ['examReadiness', userId],
    queryFn: async () => {
      if (!userId) throw new Error('User not authenticated');

      // First get performance history
      const { data: performanceData, error: performanceError } = await supabase
        .from('performance_history')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: true });

      if (performanceError) throw performanceError;

      // Calculate readiness based on recent performance
      const recentPerformance = performanceData?.slice(-5) || [];
      const avgScore = recentPerformance.reduce((acc, curr) => acc + (curr.score || 0), 0) / (recentPerformance.length || 1);
      
      // Get predictions if available
      const { data: predictions, error: predictionsError } = await supabase
        .from('performance_predictions')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (predictionsError && !predictionsError.message.includes('No rows found')) {
        console.error('Predictions error:', predictionsError);
      }

      return {
        readiness: avgScore,
        confidence: predictions?.confidence_level || 0.75,
        recommendations: predictions?.recommendations || [
          'Review previous quiz materials',
          'Focus on challenging topics',
          'Practice with more questions',
          'Take regular breaks to maintain focus'
        ],
        trendData: performanceData || []
      };
    },
    enabled: !!userId,
  });

  if (error) {
    toast.error('Failed to load exam readiness prediction');
    return null;
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-6">
        <Brain className="w-6 h-6 text-purple-500" />
        <h2 className="text-2xl font-semibold">Exam Readiness Predictor</h2>
      </div>

      {isLoading ? (
        <div className="animate-pulse space-y-4">
          <div className="h-4 bg-gray-200 rounded w-3/4"></div>
          <div className="h-48 bg-gray-200 rounded"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2"></div>
        </div>
      ) : data ? (
        <div className="space-y-6">
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <span className="text-sm font-medium">Readiness Score</span>
              <span className="text-2xl font-bold">{Math.round(data.readiness)}%</span>
            </div>
            <Progress value={data.readiness} className="h-3" />
            <p className="text-sm text-gray-500">
              Confidence Level: {Math.round(data.confidence * 100)}%
            </p>
          </div>

          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trendData}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
                <XAxis 
                  dataKey="created_at"
                  tickFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <YAxis domain={[0, 100]} />
                <Tooltip 
                  formatter={(value: number) => [`${Math.round(value)}%`, 'Score']}
                  labelFormatter={(date) => new Date(date).toLocaleDateString()}
                />
                <Area
                  type="monotone"
                  dataKey="score"
                  stroke="#8b5cf6"
                  fill="#8b5cf680"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-500" />
              <h3 className="font-semibold">AI Recommendations</h3>
            </div>
            <ul className="space-y-2">
              {data.recommendations.map((rec, index) => (
                <li key={index} className="flex items-start gap-2 text-sm">
                  <AlertCircle className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <span>{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : (
        <p className="text-gray-500">
          Complete more quizzes to receive exam readiness predictions
        </p>
      )}
    </Card>
  );
};

export default PredictiveAnalysis;
